import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
  type SelectChangeEvent,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { authedFetch } from '../../utils/apiUtils';
import { API_URL } from '../../config';

interface LogFile {
  filename: string;
  size: number;
  modified: string;
}

interface LogEntry {
  level: string;
  message: string;
  timestamp?: string;
  stack?: string;
  [key: string]: unknown;
}

interface LogResponse {
  filename: string;
  totalLines: number;
  offset: number;
  lines: number;
  hasMore: boolean;
  entries: LogEntry[];
}

const LEVEL_TONE: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  error: 'error',
  warn: 'warning',
  info: 'info',
  debug: 'default',
  raw: 'default',
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (input: string | null | undefined): string => {
  if (!input) return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ENTRY_DETAIL_SKIP = new Set(['level', 'message', 'timestamp', 'stack', 'service']);

const getEntryDetail = (entry: LogEntry): string => {
  const extra = Object.entries(entry)
    .filter(([key]) => !ENTRY_DETAIL_SKIP.has(key))
    .map(([key, val]) => `${key}=${typeof val === 'object' ? JSON.stringify(val) : val}`)
    .join(', ');
  return [entry.stack, extra].filter(Boolean).join('\n');
};

export const LogViewerPage: React.FC = () => {
  const [files, setFiles] = useState<LogFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [logData, setLogData] = useState<LogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const logFetchSeqId = useRef(0);

  const visibleFiles = useMemo(
    () => files.filter((f) => f.filename.endsWith('.log')),
    [files],
  );

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authedFetch(`${API_URL}/logs/files`);
      if (!res.ok) throw new Error('Failed to fetch log files');
      const data = await res.json();
      setFiles(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load log files');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLog = useCallback(
    async (filename: string, offset = 0) => {
      const seqId = ++logFetchSeqId.current;
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ lines: '200', offset: String(offset) });
        if (levelFilter) params.set('level', levelFilter);
        if (searchQuery) params.set('search', searchQuery);

        const res = await authedFetch(`${API_URL}/logs/view/${filename}?${params}`);
        if (!res.ok) throw new Error('Failed to fetch log data');
        const data: LogResponse = await res.json();
        if (seqId !== logFetchSeqId.current) return;

        if (offset > 0) {
          setLogData((prev) =>
            prev
              ? {
                  ...data,
                  entries: [...prev.entries, ...data.entries],
                  offset: data.offset,
                }
              : data,
          );
        } else {
          setLogData(data);
        }
      } catch (err) {
        if (seqId !== logFetchSeqId.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load log data');
      } finally {
        if (seqId === logFetchSeqId.current) {
          setLoading(false);
        }
      }
    },
    [levelFilter, searchQuery],
  );

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (selectedFile) {
      fetchLog(selectedFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelFilter, searchQuery]);

  const handleFileSelect = (filename: string) => {
    setSelectedFile(filename);
    setLevelFilter('');
    setSearchQuery('');
    setSearchInput('');
    fetchLog(filename);
  };

  const handleBack = () => {
    setSelectedFile(null);
    setLogData(null);
    setLevelFilter('');
    setSearchQuery('');
    setSearchInput('');
  };

  const handleRefresh = () => {
    if (selectedFile) {
      fetchLog(selectedFile);
    } else {
      fetchFiles();
    }
  };

  const handleLevelChange = (e: SelectChangeEvent) => {
    setLevelFilter(e.target.value);
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const handleLoadMore = () => {
    if (selectedFile && logData) {
      fetchLog(selectedFile, logData.offset + logData.lines);
    }
  };

  if (!selectedFile) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Application Logs
          </Typography>
          <Tooltip title="Refresh">
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'primary.main' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : visibleFiles.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No log files found.</Typography>
          </Paper>
        ) : (
          <Paper sx={{ p: 0, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell align="right">Last modified</TableCell>
                    <TableCell align="right">{''}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleFiles.map((file) => (
                    <TableRow
                      key={file.filename}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleFileSelect(file.filename)}
                    >
                      <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 500 }}>
                        {file.filename}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                        {formatBytes(file.size)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {formatDateTime(file.modified)}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileSelect(file.filename);
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to files">
            <IconButton
              onClick={handleBack}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'primary.main' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
            {selectedFile}
          </Typography>
          {logData && (
            <Chip
              label={`${logData.totalLines.toLocaleString()} lines`}
              size="small"
              variant="outlined"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            />
          )}
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              color: 'text.secondary',
              '&:hover, &.Mui-focusVisible': { color: 'primary.main' },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Level</InputLabel>
            <Select value={levelFilter} onChange={handleLevelChange} label="Level">
              <MenuItem value="">All levels</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warn">Warn</MenuItem>
              <MenuItem value="info">Info</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search logs..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" color="inherit" size="small" onClick={handleSearch}>
            Search
          </Button>
        </Box>
      </Paper>

      {loading && !logData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : logData && logData.entries.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No log entries match the current filters.</Typography>
        </Paper>
      ) : logData ? (
        <>
          <Paper sx={{ p: 0, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 180 }}>Timestamp</TableCell>
                    <TableCell sx={{ width: 80 }}>Level</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logData.entries.map((entry, i) => {
                    const detail = getEntryDetail(entry);
                    return (
                      <TableRow
                        key={i}
                        sx={
                          entry.level === 'error'
                            ? { backgroundColor: 'rgba(220, 38, 38, 0.04)' }
                            : undefined
                        }
                      >
                        <TableCell
                          sx={{
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            fontSize: '0.8125rem',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'top',
                            color: 'text.secondary',
                          }}
                        >
                          {formatDateTime(entry.timestamp)}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Chip
                            label={entry.level}
                            size="small"
                            color={LEVEL_TONE[entry.level] || 'default'}
                            variant="outlined"
                            sx={{ fontWeight: 500, fontSize: '0.7rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              fontSize: '0.8125rem',
                              wordBreak: 'break-word',
                            }}
                          >
                            {entry.message}
                          </Typography>
                          {detail && (
                            <Typography
                              variant="caption"
                              component="pre"
                              sx={{
                                mt: 0.5,
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                fontSize: '0.7rem',
                                color: 'text.secondary',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {detail}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {logData.hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleLoadMore}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {loading ? 'Loading' : 'Load more'}
              </Button>
            </Box>
          )}
        </>
      ) : null}
    </Box>
  );
};
